import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { Dropdown } from "semantic-ui-react";
import {
  SG_GET_ORGANIZATION_GROUP,
  SG_ADD_ORGANIZATION_GROUP,
  SG_EDIT_ORGANIZATION_GROUP,
  SG_DELETE_ORGANIZATION_GROUP,
  SG_BULK_UPDATE_ORGANIZATION_GROUP,
  SG_ORG_GROUP_ADD_BY_CATEGORY,
  SG_ORG_GROUP_REMOVE_BY_CATEGORY,
  SG_EMPLOYEE_CATEGORY_SEARCH,
  LOAD_EMPLOYEES,
  SG_GET_EMPLOYEE_ORG_CATEGORIES,
  SG_GET_SURVEY_STRUCTURES,
  SG_FETCH_ORGANIZATION_GROUP,
  SG_UPDATE_EMPLOYEE_AUDIT,
  PULL_EMPLOYEE,
} from "../../constants/actions";

import TabsComponent from './TabsComponent';
import ManagerPermissions from './ManagerPermissions';
import AccessBreadcrumb from "./AccessBreadcrumb";

// Icons
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
  </svg>
);

const UsersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
  </svg>
);

const UserIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 2H6C4.89 2 4 2.89 4 4V20C4 21.11 4.89 22 6 22H18C19.11 22 20 21.11 20 20V9H21Z"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
  </svg>
);

const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
  </svg>
);

// Custom Confirmation Dialog Component
const ConfirmationDialog = ({ isOpen, onClose, onConfirm, title, message, confirmText = "Remove", cancelText = "Cancel" }) => {
  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ConfirmationContent onClick={e => e.stopPropagation()}>
        <ConfirmationHeader>
          <ConfirmationTitle>{title}</ConfirmationTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ConfirmationHeader>
        
        <ConfirmationBody>
          <ConfirmationMessage>{message}</ConfirmationMessage>
        </ConfirmationBody>
        
        <ConfirmationActions>
          <CancelButton onClick={onClose}>
            {cancelText}
          </CancelButton>
          <ConfirmButton onClick={onConfirm}>
            {confirmText}
          </ConfirmButton>
        </ConfirmationActions>
      </ConfirmationContent>
    </ModalOverlay>
  );
};

const GroupNameModal = ({
  isOpen,
  initialValue = "",
  onCancel,
  onSubmit,
  isBusy = false,
}) => {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setValue(initialValue || "");
      setError("");
    }
  }, [initialValue, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setError("Group name is required");
      return;
    }
    onSubmit(trimmed);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <ModalOverlay onClick={onCancel}>
      <GroupNameModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Group Name</ModalTitle>
          <CloseButton onClick={onCancel}>×</CloseButton>
        </ModalHeader>
        <GroupNameBody>
          <GroupNameDescription>
            Please give a group name first
          </GroupNameDescription>
          <GroupNameInput
            type="text"
            value={value}
            onChange={(event) => {
              setValue(event.target.value);
              if (error) {
                setError("");
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder="e.g. Customer Success Managers"
            autoFocus
          />
          {error && <GroupNameError>{error}</GroupNameError>}
        </GroupNameBody>
        <GroupNameActions>
          <CancelButton onClick={onCancel}>Cancel</CancelButton>
          <SaveButton onClick={handleSubmit} disabled={!value.trim() || isBusy}>
            {isBusy ? "Saving..." : "Continue"}
          </SaveButton>
        </GroupNameActions>
      </GroupNameModalContent>
    </ModalOverlay>
  );
};

// Enhanced Add Contacts Modal Component with bulk email paste and category filtering
const AddEmployeesModal = ({
  isOpen,
  onClose,
  group,
  groupName,
  employees = [],
  onAddEmployees,
}) => {
  const dispatch = useDispatch();
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeeSourceMap, setEmployeeSourceMap] = useState({}); // Maps employeeId to { source: "bulk_category", categoryName, categoryValue }
  const [searchTerm, setSearchTerm] = useState("");
  const [bulkEmailText, setBulkEmailText] = useState("");
  const [invalidEmails, setInvalidEmails] = useState([]);
  const [activeTab, setActiveTab] = useState("individual"); // "individual", "bulk", "category"
  const [bulkCategoryId, setBulkCategoryId] = useState(null);
  const [bulkCategoryValueId, setBulkCategoryValueId] = useState(null);

  // Get employee categories and survey structure from Redux
  const {
    get_employee_org_category,
    get_survey_structure,
    categoryPreview,
    orgGroupPending,
    employeeCategorySearch,
  } = useSelector(
    (state) => ({
      get_employee_org_category: Array.isArray(
        state.employee_category?.employee_org_category
      )
        ? state.employee_category.employee_org_category
        : [],
      get_survey_structure: state.surveystructure?.survey_structure || [],
      categoryPreview: state.organization_group?.categoryPreview,
      orgGroupPending: state.organization_group?.pending,
      employeeCategorySearch: state.employee_category?.search,
    }),
    shallowEqual
  );

  // Build category filter options from survey structure
  const filterCategories = useMemo(() => {
    if (!get_survey_structure?.[0]?.categories) return [];

    return get_survey_structure[0].categories
      .filter((cat) => cat.visibility !== "private" && !cat.demographic)
      .map((cat) => ({
        id: cat.id,
        name: cat.name,
        priority: cat.priority,
        options: cat.options.map((opt) => ({
          id: opt.id,
          name: opt.name,
          text: opt.text || opt.name,
        })),
      }));
  }, [get_survey_structure]);

  // Derive selected category + option objects
  const selectedBulkCategory = useMemo(
    () => filterCategories.find((cat) => cat.id === bulkCategoryId) || null,
    [filterCategories, bulkCategoryId]
  );
  const selectedBulkCategoryValue = useMemo(
    () =>
      selectedBulkCategory?.options?.find(
        (opt) => opt.id === bulkCategoryValueId
      ) || null,
    [selectedBulkCategory, bulkCategoryValueId]
  );

  // Sorted options for the Value dropdown (alphabetically by text)
  const sortedCategoryValueOptions = useMemo(() => {
    if (!selectedBulkCategory?.options) return [];
    return [...selectedBulkCategory.options]
      .sort((a, b) => {
        const textA = (a.text || a.name || '').toLowerCase();
        const textB = (b.text || b.name || '').toLowerCase();
        return textA.localeCompare(textB);
      })
      .map((opt) => ({
        key: opt.id,
        text: opt.text || opt.name || '',
        value: opt.id,
      }));
  }, [selectedBulkCategory]);

  // Filter employees based on search term
  const filteredEmployees = useMemo(() => {
    if (!Array.isArray(employees)) return [];

    let filtered = employees.filter((emp) => emp.status === 1); // Only active employees

    if (searchTerm) {
      filtered = filtered.filter(
        (emp) =>
          emp.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          emp.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          emp.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [employees, searchTerm]);

  const handleEmployeeToggle = (employeeId) => {
    setSelectedEmployees((prev) =>
      prev.includes(employeeId)
        ? prev.filter((id) => id !== employeeId)
        : [...prev, employeeId]
    );
    // Remove source metadata when employee is removed
    if (employeeSourceMap[employeeId]) {
      setEmployeeSourceMap((prev) => {
        const newMap = { ...prev };
        delete newMap[employeeId];
        return newMap;
      });
    }
  };

  // Helpers for preview/confirm state
  const isCategoryPreviewForThisGroup =
    categoryPreview?.groupId === group?.id &&
    categoryPreview?.mode === "add" &&
    categoryPreview?.stage === "preview";
  const isCategoryConfirmForThisGroup =
    categoryPreview?.groupId === group?.id &&
    categoryPreview?.mode === "add" &&
    categoryPreview?.stage === "confirmed";
  const bulkMatchCount = categoryPreview?.match_count || 0;
  const bulkMessage = categoryPreview?.message || null;
  const bulkLoading = !!orgGroupPending || !!employeeCategorySearch?.pending;

  // Update selectedEmployees when confirmed response comes in with added_employee_ids
  useEffect(() => {
    if (isCategoryConfirmForThisGroup) {
      // Use added_employee_ids from confirmed response if available, otherwise use employee_ids
      const employeeIdsToAdd = categoryPreview?.added_employee_ids || categoryPreview?.employee_ids || [];
      if (employeeIdsToAdd.length > 0) {
        setSelectedEmployees((prev) => {
          const newIds = employeeIdsToAdd.filter((id) => !prev.includes(id));
          return [...prev, ...newIds];
        });
        // Track source for each employee
        setEmployeeSourceMap((prev) => {
          const newMap = { ...prev };
          employeeIdsToAdd.forEach((id) => {
            newMap[id] = {
              source: "bulk_category",
              categoryName: selectedBulkCategory?.name || "",
              categoryValue: selectedBulkCategoryValue?.text || selectedBulkCategoryValue?.name || "",
            };
          });
          return newMap;
        });
      }
    }
  }, [isCategoryConfirmForThisGroup, categoryPreview, selectedBulkCategory, selectedBulkCategoryValue]);

  // Handlers for bulk category preview and confirm
  const handleBulkCategoryPreview = () => {
    if (!group?.id || !selectedBulkCategory || !selectedBulkCategoryValue) {
      return;
    }
    const filtersArray = [
      {
        // Use the most stable IDs you have – here we assume category.id and option.id map to name_id/value_id.
        category_name_id: selectedBulkCategory.id,
        category_value_id: selectedBulkCategoryValue.id,
        priority: selectedBulkCategory.priority,
      },
    ];
    const payloadBase = {
      organization_group_id: group.id,
      category_filters: filtersArray,
      confirm: false,
    };
    dispatch({
      type: SG_ORG_GROUP_ADD_BY_CATEGORY,
      payload: payloadBase,
    });
    // Optional: also load the underlying EmployeeCategory rows for richer UI/future use
    dispatch({
      type: SG_EMPLOYEE_CATEGORY_SEARCH,
      payload: {
        page: 1,
        page_size: 10000,
        category_name_id: selectedBulkCategory.id,
        category_value_id: selectedBulkCategoryValue.id,
        category_priority: selectedBulkCategory.priority,
        ordering: "employee__first_name",
      },
    });
  };

  const handleBulkCategoryConfirm = () => {
    if (!group?.id || !selectedBulkCategory || !selectedBulkCategoryValue) {
      return;
    }
    const filtersArray = [
      {
        category_name_id: selectedBulkCategory.id,
        category_value_id: selectedBulkCategoryValue.id,
        priority: selectedBulkCategory.priority,
      },
    ];
    const payloadBase = {
      organization_group_id: group.id,
      category_filters: filtersArray,
      confirm: true,
    };
    dispatch({
      type: SG_ORG_GROUP_ADD_BY_CATEGORY,
      payload: payloadBase,
    });
  // Employees will be added to selectedEmployees via useEffect when confirmed response arrives
  };


  const handleBulkEmailProcess = () => {
    if (!bulkEmailText.trim()) return;

    // Extract emails from pasted text (split by comma, semicolon, newline, or space)
    const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
    const extractedEmails = bulkEmailText.match(emailRegex) || [];

    const foundEmployees = [];
    const notFoundEmails = [];

    extractedEmails.forEach((email) => {
      const employee = employees.find(
        (emp) =>
          emp.email?.toLowerCase() === email.toLowerCase() && emp.status === 1
      );

      if (employee) {
        foundEmployees.push(employee.id);
      } else {
        notFoundEmails.push(email);
      }
    });

    // Add found employees to selection
    setSelectedEmployees((prev) => {
      const newSelections = foundEmployees.filter((id) => !prev.includes(id));
      return [...prev, ...newSelections];
    });

    // Set invalid emails
    setInvalidEmails(notFoundEmails);

    // Clear bulk text
    setBulkEmailText("");
  };

  const handleSubmit = () => {
    if (selectedEmployees.length > 0) {
      // ALWAYS use bulk API - no matter how many employees
      console.log(
        "Modal submitting contacts:",
        selectedEmployees,
        "to group:",
        group.id
      );
      onAddEmployees(group.id, selectedEmployees, true); // Always use bulk API

      setSelectedEmployees([]);
      setSearchTerm("");
      setBulkEmailText("");
      setInvalidEmails([]);
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedEmployees([]);
    setEmployeeSourceMap({});
    setSearchTerm("");
    setBulkEmailText("");
    setInvalidEmails([]);
    setActiveTab("individual");
    setBulkCategoryId(null);
    setBulkCategoryValueId(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <EnhancedModalOverlay onClick={handleClose}>
      <EnhancedModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            Assign Contacts to {groupName || group?.name || "Group"}
          </ModalTitle>
          <HeaderActionButtons>
            <CancelButton onClick={handleClose}>Cancel</CancelButton>
            <SaveButton
              onClick={handleSubmit}
              disabled={selectedEmployees.length === 0}
            >
              Assign {selectedEmployees.length} Contact
              {selectedEmployees.length !== 1 ? "s" : ""}
            </SaveButton>
          </HeaderActionButtons>
        </ModalHeader>

        <EnhancedModalBody>
          <LeftPanel>
            <PanelContent>
              <TabsContainer>
                <Tab
                  active={activeTab === "individual"}
                  onClick={() => {
                    setActiveTab("individual");
                    setSearchTerm(""); // Clear search when switching tabs
                  }}
                >
                  Individual Selection
                </Tab>
                <Tab
                  active={activeTab === "bulk"}
                  onClick={() => {
                    setActiveTab("bulk");
                    setSearchTerm(""); // Clear search when switching tabs
                  }}
                >
                  Bulk Email Paste
                </Tab>
                <Tab
                  active={activeTab === "category"}
                  onClick={() => {
                    setActiveTab("category");
                    setSearchTerm(""); // Clear search when switching tabs
                  }}
                >
                  By Tag
                </Tab>
              </TabsContainer>

              {activeTab === "individual" && (
                <>
                  <SearchContainer>
                    <SearchLabel>Find Contacts</SearchLabel>
                    <SearchInputWrapper>
                      <SearchIconWrapper>
                        <SearchIcon />
                      </SearchIconWrapper>
                      <SearchInput
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Start typing to search contacts..."
                      />
                    </SearchInputWrapper>
                    <SearchHelpText>
                      {searchTerm ? (
                        <SearchResultsCount>
                          {filteredEmployees.length} contact
                          {filteredEmployees.length !== 1 ? "s" : ""} found
                        </SearchResultsCount>
                      ) : (
                        <TotalEmployeesCount>
                          {employees.length} total contacts available • Start
                          typing to search
                        </TotalEmployeesCount>
                      )}
                    </SearchHelpText>
                  </SearchContainer>

                  {searchTerm ? (
                    <EmployeesList>
                      {filteredEmployees.map((emp) => (
                        <EmployeeItem
                          key={emp.id}
                          selected={selectedEmployees.includes(emp.id)}
                          onClick={() => handleEmployeeToggle(emp.id)}
                        >
                          <EmployeeCheckbox
                            type="checkbox"
                            checked={selectedEmployees.includes(emp.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleEmployeeToggle(emp.id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <EmployeeDetails>
                            <EmployeeName>
                              {emp.first_name} {emp.last_name}
                            </EmployeeName>
                            <EmployeeEmail>{emp.email}</EmployeeEmail>
                            {emp.role_name && (
                              <EmployeeRole>{emp.role_name}</EmployeeRole>
                            )}
                          </EmployeeDetails>
                          {selectedEmployees.includes(emp.id) && (
                            <SelectedIndicator>✓</SelectedIndicator>
                          )}
                        </EmployeeItem>
                      ))}
                      {filteredEmployees.length === 0 && (
                        <ModalEmptyState>
                          <EmptySearchIcon>🔍</EmptySearchIcon>
                          <EmptyTitle>No contacts found</EmptyTitle>
                          <EmptyText>Try adjusting your search terms</EmptyText>
                        </ModalEmptyState>
                      )}
                    </EmployeesList>
                  ) : (
                    <SearchPromptState>
                      <SearchPromptIcon>👥</SearchPromptIcon>
                      <SearchPromptTitle>
                        Ready to find contacts
                      </SearchPromptTitle>
                      <SearchPromptText>
                        Start typing in the search box above to find contacts by
                        name or email.
                        <br />
                        Perfect for large organizations with thousands of
                        contacts.
                      </SearchPromptText>
                      <SearchPromptFeatures>
                        <SearchFeature>
                          • Search by first name, last name, or email
                        </SearchFeature>
                        <SearchFeature>
                          • Real-time filtering as you type
                        </SearchFeature>
                        <SearchFeature>
                          • Select multiple contacts at once
                        </SearchFeature>
                      </SearchPromptFeatures>
                    </SearchPromptState>
                  )}
                </>
              )}

              {activeTab === "bulk" && (
                <BulkEmailSection>
                  <BulkEmailHeader>
                    <BulkEmailIcon>📧</BulkEmailIcon>
                    <BulkEmailTitleSection>
                      <BulkEmailLabel>Paste Email Addresses</BulkEmailLabel>
                      <BulkEmailHint>
                        Perfect for adding multiple contacts at once. Supports
                        various formats.
                      </BulkEmailHint>
                    </BulkEmailTitleSection>
                  </BulkEmailHeader>

                  <BulkEmailInstructions>
                    <InstructionTitle>Supported Formats:</InstructionTitle>
                    <FormatExamples>
                      <FormatExample>
                        • Comma separated: john@company.com, jane@company.com
                      </FormatExample>
                      <FormatExample>
                        • Semicolon separated: john@company.com;
                        jane@company.com
                      </FormatExample>
                      <FormatExample>
                        • Line separated (one per line)
                      </FormatExample>
                      <FormatExample>
                        • Mixed formats in the same input
                      </FormatExample>
                    </FormatExamples>
                  </BulkEmailInstructions>

                  <BulkEmailTextarea
                    value={bulkEmailText}
                    onChange={(e) => setBulkEmailText(e.target.value)}
                    placeholder={`john@company.com, jane@company.com
mike@company.com
sarah@company.com; david@company.com

Paste any combination of emails here...`}
                  />

                  <BulkEmailActions>
                    <BulkEmailStats>
                      {bulkEmailText && (
                        <EmailCount>
                          {
                            bulkEmailText
                              .split(/[,;\n\r\s]+/)
                              .filter(
                                (email) => email.trim() && email.includes("@")
                              ).length
                          }{" "}
                          emails detected
                        </EmailCount>
                      )}
                    </BulkEmailStats>
                    <ProcessEmailsButton
                      onClick={handleBulkEmailProcess}
                      disabled={!bulkEmailText.trim()}
                    >
                      Process Emails
                    </ProcessEmailsButton>
                  </BulkEmailActions>

                  {invalidEmails.length > 0 && (
                    <InvalidEmailsSection>
                      <InvalidEmailsHeader>
                        <InvalidEmailsIcon>⚠️</InvalidEmailsIcon>
                        <InvalidEmailsTitle>
                          Emails Not Found ({invalidEmails.length})
                        </InvalidEmailsTitle>
                      </InvalidEmailsHeader>
                      <InvalidEmailsList>
                        {invalidEmails.map((email, index) => (
                          <InvalidEmail key={index}>{email}</InvalidEmail>
                        ))}
                      </InvalidEmailsList>
                      <InvalidEmailsHint>
                        These emails don't match any active contacts in your
                        organization. Double-check for typos or ensure these
                        contacts have been added to the system.
                      </InvalidEmailsHint>
                    </InvalidEmailsSection>
                  )}
                </BulkEmailSection>
              )}

              {activeTab === "category" && (
                <CategorySection>
                  <CategoryHeader>
                    <CategoryIcon>🏷️</CategoryIcon>
                    <CategoryTitleSection>
                      <CategoryLabel>
                        Filter by Contact Tags
                      </CategoryLabel>
                      <CategoryHint>
                        Select tags to quickly find and add contacts by
                        department, location, role, or other attributes.
                      </CategoryHint>
                    </CategoryTitleSection>
                  </CategoryHeader>

                  {/* Bulk by Category (new) */}
                  {filterCategories.length > 0 && (
                    <BulkCategoryBar>
                      <BulkCategoryRow>
                        <BulkCategoryLabel>Bulk manage by tag</BulkCategoryLabel>
                      </BulkCategoryRow>
                      <BulkCategorySelectors>
                        <BulkSelect>
                          <BulkSelectLabel>Category</BulkSelectLabel>
                          <BulkNativeSelect
                            value={bulkCategoryId || ""}
                            onChange={(e) => {
                              const value = e.target.value || null;
                              setBulkCategoryId(value ? value : null);
                              setBulkCategoryValueId(null);
                            }}
                          >
                            <option value="">Select category…</option>
                            {filterCategories.map((cat) => (
                              <option key={cat.id} value={cat.id}>
                                {cat.name}
                              </option>
                            ))}
                          </BulkNativeSelect>
                        </BulkSelect>
                        <BulkSelect>
                          <BulkSelectLabel>Value</BulkSelectLabel>
                          <Dropdown
                            selection
                            search
                            placeholder="Select value…"
                            value={bulkCategoryValueId || ""}
                            onChange={(e, { value }) =>
                              setBulkCategoryValueId(value || null)
                            }
                            disabled={!selectedBulkCategory}
                            options={sortedCategoryValueOptions}
                            fluid
                          />
                        </BulkSelect>
                      </BulkCategorySelectors>
                      <BulkCategoryActions>
                        <CategoryAddButton
                          type="button"
                          onClick={handleBulkCategoryPreview}
                          disabled={
                            !bulkCategoryId || !bulkCategoryValueId || bulkLoading
                          }
                        >
                          {bulkLoading ? "Checking..." : "Preview"}
                        </CategoryAddButton>
                        {isCategoryPreviewForThisGroup && bulkMatchCount > 0 && (
                          <CategoryAddButton
                            type="button"
                            onClick={handleBulkCategoryConfirm}
                            disabled={bulkLoading}
                          >
                            Add {bulkMatchCount} contact{bulkMatchCount !== 1 ? "s" : ""}
                          </CategoryAddButton>
                        )}
                      </BulkCategoryActions>
                      {bulkMessage && (
                        <BulkMessage>{bulkMessage}</BulkMessage>
                      )}
                      {isCategoryConfirmForThisGroup && (
                        <BulkConfirmMessage>
                          {categoryPreview.message ||
                            "Category update confirmed. Members list will refresh shortly."}
                        </BulkConfirmMessage>
                      )}
                    </BulkCategoryBar>
                  )}

                </CategorySection>
              )}
            </PanelContent>
          </LeftPanel>

          <RightPanel>
            <PanelHeader>
              <div>
                <PanelTitle>
                  Selected Contacts ({selectedEmployees.length})
                </PanelTitle>
                <PanelSubtitle>
                  {selectedEmployees.length === 0
                    ? "No contacts selected yet"
                    : `Ready to assign to ${
                        groupName || group?.name || "this group"
                      }`}
                </PanelSubtitle>
              </div>
              {selectedEmployees.length > 0 && (
                <ClearAllButton onClick={() => {
                  setSelectedEmployees([]);
                  setEmployeeSourceMap({});
                }}>
                  Clear All
                </ClearAllButton>
              )}
            </PanelHeader>

            <PanelContent>
              {selectedEmployees.length > 0 ? (
                <SelectedEmployeesGrid>
                  {selectedEmployees.map((empId) => {
                    const emp = employees.find((e) => e.id === empId);
                    const sourceInfo = employeeSourceMap[empId];
                    return emp ? (
                      <SelectedEmployeeCard key={empId}>
                        <SelectedEmployeeDetails>
                          <SelectedEmployeeName>
                            {emp.first_name} {emp.last_name}
                          </SelectedEmployeeName>
                          <SelectedEmployeeEmail>
                            {emp.email}
                          </SelectedEmployeeEmail>
                          {emp.role_name && (
                            <SelectedEmployeeRole>
                              {emp.role_name}
                            </SelectedEmployeeRole>
                          )}
                        </SelectedEmployeeDetails>
                        <RemoveSelectedEmployeeButton
                          onClick={() => handleEmployeeToggle(empId)}
                        >
                          ×
                        </RemoveSelectedEmployeeButton>
                      </SelectedEmployeeCard>
                    ) : null;
                  })}
                </SelectedEmployeesGrid>
              ) : (
                <EmptySelectedState>
                  <EmptySelectedIcon>👥</EmptySelectedIcon>
                  <EmptySelectedTitle>No contacts selected</EmptySelectedTitle>
                  <EmptySelectedText>
                    Use the filters on the left to find and select contacts to
                    add to this group.
                  </EmptySelectedText>
                </EmptySelectedState>
              )}
            </PanelContent>
          </RightPanel>
        </EnhancedModalBody>
      </EnhancedModalContent>
    </EnhancedModalOverlay>
  );
};

// Remove Contacts by Tag Modal - Simplified removal-focused view
const RemoveContactsByTagModal = ({
  isOpen,
  onClose,
  group,
  groupName,
  currentGroupMembers = [],
  employees = [],
}) => {
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const [bulkCategoryId, setBulkCategoryId] = useState(null);
  const [bulkCategoryValueId, setBulkCategoryValueId] = useState(null);

  // Get employee categories and survey structure from Redux
  const {
    get_survey_structure,
    categoryPreview,
    orgGroupPending,
  } = useSelector(
    (state) => ({
      get_survey_structure: state.surveystructure?.survey_structure || [],
      categoryPreview: state.organization_group?.categoryPreview,
      orgGroupPending: state.organization_group?.pending || false,
    }),
    shallowEqual
  );

  // Build category filter options from survey structure
  const filterCategories = useMemo(() => {
    if (!get_survey_structure?.[0]?.categories) return [];

    return get_survey_structure[0].categories
      .filter((cat) => cat.visibility !== "private" && !cat.demographic)
      .map((cat) => ({
        id: cat.id,
        name: cat.name,
        priority: cat.priority,
        options: cat.options.map((opt) => ({
          id: opt.id,
          name: opt.name,
          text: opt.text || opt.name,
        })),
      }));
  }, [get_survey_structure]);

  // Derive selected category + option objects
  const selectedBulkCategory = useMemo(
    () => filterCategories.find((cat) => cat.id === bulkCategoryId) || null,
    [filterCategories, bulkCategoryId]
  );
  const selectedBulkCategoryValue = useMemo(
    () =>
      selectedBulkCategory?.options?.find(
        (opt) => opt.id === bulkCategoryValueId
      ) || null,
    [selectedBulkCategory, bulkCategoryValueId]
  );

  // Sorted options for the Value dropdown
  const sortedCategoryValueOptions = useMemo(() => {
    if (!selectedBulkCategory?.options) return [];
    return [...selectedBulkCategory.options]
      .sort((a, b) => {
        const textA = (a.text || a.name || '').toLowerCase();
        const textB = (b.text || b.name || '').toLowerCase();
        return textA.localeCompare(textB);
      })
      .map((opt) => ({
        key: opt.id,
        text: opt.text || opt.name || '',
        value: opt.id,
      }));
  }, [selectedBulkCategory]);

  // Check if preview is for this group
  const isCategoryRemovePreviewForThisGroup =
    categoryPreview?.groupId === group?.id &&
    categoryPreview?.mode === "remove" &&
    categoryPreview?.stage === "preview";
  const matchCount = categoryPreview?.match_count || 0;
  const employeesToRemove = categoryPreview?.employee_ids || [];
  const bulkLoading = !!orgGroupPending;

  // Get employees to remove (with full employee objects) - only those already in the group
  const employeesToRemoveObjects = useMemo(() => {
    if (!Array.isArray(employees) || employeesToRemove.length === 0 || !Array.isArray(currentGroupMembers)) return [];
    const removeIdsSet = new Set(employeesToRemove);
    const currentGroupMemberIds = new Set(currentGroupMembers.map(m => m.id));
    // Only include employees that are both in the removal list AND currently in the group
    return employees.filter((emp) => 
      emp?.id && 
      removeIdsSet.has(emp.id) && 
      currentGroupMemberIds.has(emp.id)
    );
  }, [employeesToRemove, employees, currentGroupMembers]);

  // Calculate updated list (current members minus those to be removed)
  const updatedGroupMembers = useMemo(() => {
    if (!isCategoryRemovePreviewForThisGroup) return currentGroupMembers;
    const removeIdsSet = new Set(employeesToRemove);
    return currentGroupMembers.filter((member) => !removeIdsSet.has(member.id));
  }, [currentGroupMembers, employeesToRemove, isCategoryRemovePreviewForThisGroup]);

  const handlePreview = () => {
    if (!group?.id || !selectedBulkCategory || !selectedBulkCategoryValue) return;
    dispatch({
      type: SG_ORG_GROUP_REMOVE_BY_CATEGORY,
      payload: {
        organization_group_id: group.id,
        filters: {
          category_name_id: selectedBulkCategory.id,
          category_value_id: selectedBulkCategoryValue.id,
          priority: selectedBulkCategory.priority,
        },
        confirm: false,
      },
    });
  };

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleConfirm = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmRemoval = () => {
    if (!group?.id || !selectedBulkCategory || !selectedBulkCategoryValue) return;
    dispatch({
      type: SG_ORG_GROUP_REMOVE_BY_CATEGORY,
      payload: {
        organization_group_id: group.id,
        filters: {
          category_name_id: selectedBulkCategory.id,
          category_value_id: selectedBulkCategoryValue.id,
          priority: selectedBulkCategory.priority,
        },
        confirm: true,
        remove_tokens: true,
      },
    });
    setShowConfirmDialog(false);
    onClose();
  };

  const handleClose = () => {
    setBulkCategoryId(null);
    setBulkCategoryValueId(null);
    setShowConfirmDialog(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <EnhancedModalOverlay onClick={handleClose}>
      <RemoveModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Remove Contacts by Tag</ModalTitle>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>

        <RemoveModalBody>
          {/* Category Selection */}
          <CategorySelectionSection>
            <CategorySelectionLabel>Select Category to Remove</CategorySelectionLabel>
            <CategorySelectionRow>
              <CategorySelectWrapper>
                <CategorySelectLabel>Category</CategorySelectLabel>
                <BulkNativeSelect
                  value={bulkCategoryId || ""}
                  onChange={(e) => {
                    const value = e.target.value || null;
                    setBulkCategoryId(value ? value : null);
                    setBulkCategoryValueId(null);
                  }}
                >
                  <option value="">Select category…</option>
                  {filterCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </BulkNativeSelect>
              </CategorySelectWrapper>
              <CategorySelectWrapper>
                <CategorySelectLabel>Value</CategorySelectLabel>
                <Dropdown
                  selection
                  search
                  placeholder="Select value…"
                  value={bulkCategoryValueId || ""}
                  onChange={(e, { value }) =>
                    setBulkCategoryValueId(value || null)
                  }
                  disabled={!selectedBulkCategory}
                  options={sortedCategoryValueOptions}
                  fluid
                />
              </CategorySelectWrapper>
            </CategorySelectionRow>
            <CategoryPreviewButton
              onClick={handlePreview}
              disabled={!bulkCategoryId || !bulkCategoryValueId || bulkLoading}
            >
              {bulkLoading ? "Checking..." : "Preview Removal"}
            </CategoryPreviewButton>
          </CategorySelectionSection>

          {/* Three-column layout: Current | To Remove | Updated */}
          {isCategoryRemovePreviewForThisGroup && (
            <PreviewSections>
              <PreviewColumn>
                <PreviewColumnHeader>
                  <PreviewColumnTitle>Current Contacts</PreviewColumnTitle>
                  <PreviewColumnCount>{currentGroupMembers.length}</PreviewColumnCount>
                </PreviewColumnHeader>
                <PreviewList>
                  {currentGroupMembers.map((member) => (
                    <PreviewListItem key={member.id}>
                      <PreviewItemName>
                        {member.first_name} {member.last_name}
                      </PreviewItemName>
                      <PreviewItemEmail>{member.email}</PreviewItemEmail>
                    </PreviewListItem>
                  ))}
                </PreviewList>
              </PreviewColumn>

              <PreviewColumn remove>
                <PreviewColumnHeader>
                  <PreviewColumnTitle>To Remove</PreviewColumnTitle>
                  <PreviewColumnCount remove>{employeesToRemoveObjects.length}</PreviewColumnCount>
                </PreviewColumnHeader>
                <PreviewList>
                  {employeesToRemoveObjects.map((member) => (
                    <PreviewListItem key={member.id} remove>
                      <PreviewItemName>
                        {member.first_name} {member.last_name}
                      </PreviewItemName>
                      <PreviewItemEmail>{member.email}</PreviewItemEmail>
                    </PreviewListItem>
                  ))}
                </PreviewList>
              </PreviewColumn>

              <PreviewColumn>
                <PreviewColumnHeader>
                  <PreviewColumnTitle>After Removal</PreviewColumnTitle>
                  <PreviewColumnCount>{updatedGroupMembers.length}</PreviewColumnCount>
                </PreviewColumnHeader>
                <PreviewList>
                  {updatedGroupMembers.map((member) => (
                    <PreviewListItem key={member.id}>
                      <PreviewItemName>
                        {member.first_name} {member.last_name}
                      </PreviewItemName>
                      <PreviewItemEmail>{member.email}</PreviewItemEmail>
                    </PreviewListItem>
                  ))}
                </PreviewList>
              </PreviewColumn>
            </PreviewSections>
          )}

          {categoryPreview?.message && isCategoryRemovePreviewForThisGroup && (
            <PreviewMessage>{categoryPreview.message}</PreviewMessage>
          )}
        </RemoveModalBody>

        <RemoveModalActions>
          <CancelButton onClick={handleClose}>Cancel</CancelButton>
          {isCategoryRemovePreviewForThisGroup && employeesToRemoveObjects.length > 0 && (
            <RemoveConfirmButton onClick={handleConfirm} disabled={bulkLoading}>
              Remove {employeesToRemoveObjects.length} Contact{employeesToRemoveObjects.length !== 1 ? "s" : ""}
            </RemoveConfirmButton>
          )}
        </RemoveModalActions>
      </RemoveModalContent>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmRemoval}
        title="Confirm Removal"
        message={
          `Are you sure you want to remove ${employeesToRemoveObjects.length} contact${
            employeesToRemoveObjects.length !== 1 ? "s" : ""
          } from "${groupName || group?.name || "this group"}"? This action cannot be undone.`
        }
        confirmText="Remove"
        cancelText="Cancel"
      />
    </EnhancedModalOverlay>
  );
};

const GroupDetail = ({ group, onBack }) => {
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const [isAddEmployeesModalOpen, setIsAddEmployeesModalOpen] = useState(false);
  const [isRemoveByTagModalOpen, setIsRemoveByTagModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // 10 members per page in detail view
  const [selectedMembers, setSelectedMembers] = useState(new Set());
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    employeeIds: [],
    isBulk: false,
  });
  const [activeTab, setActiveTab] = useState(0);
  const [profileFirstName, setProfileFirstName] = useState("");
  const [profileLastName, setProfileLastName] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [isGroupNameModalOpen, setIsGroupNameModalOpen] = useState(false);
  const [groupName, setGroupName] = useState(group?.name || "");
  const [groupNameTargetAction, setGroupNameTargetAction] = useState(null);
  const [pendingGroupName, setPendingGroupName] = useState("");
  const [isInlineGroupNameEditing, setIsInlineGroupNameEditing] = useState(false);
  const [inlineGroupNameValue, setInlineGroupNameValue] = useState("");
  const [inlineGroupNameError, setInlineGroupNameError] = useState("");
  const [groupNameSaving, setGroupNameSaving] = useState(false);

  // Redux state
  const {
    organization_groups,
    employees,
    pending,
    get_auth,
    organization_group_detail,
  } = useSelector(
    (state) => ({
      organization_groups: Array.isArray(
        state.organization_group?.organization_groups
      )
        ? state.organization_group.organization_groups
        : [],
      employees: Array.isArray(state.employees?.employees)
        ? state.employees.employees
        : [],
      pending: state.organization_group?.pending || false,
      get_auth: state.auth,
      organization_group_detail: state.organization_group?.organization_group,
    }),
    shallowEqual
  );

  // Helper function to get employee by ID or return manager object if it's already populated
  const getEmployeeById = (managerData) => {
    if (!managerData) return null;
    
    // If managerData is already an object with employee details, return it directly
    if (typeof managerData === 'object' && managerData.id) {
      return managerData;
    }
    
    // If managerData is just an ID, look it up in the employees array
    if (Array.isArray(employees) && typeof managerData === 'number') {
      return employees.find(emp => emp.id === parseInt(managerData));
    }
    
    return null;
  };

  // Resolve manager early so effects and handlers can reference it safely
  const manager = getEmployeeById(group?.manager);
  const canEditGroupName = Boolean(group?.id && !group?.isTemporary);
  const managerFullName = manager
    ? `${manager.first_name || ""} ${manager.last_name || ""}`.trim()
    : "";
  const derivedGroupName =
    (groupName && groupName.trim()) ||
    managerFullName ||
    manager?.email ||
    "";

  useEffect(() => {
    if (!group && typeof onBack === "function") {
      onBack();
    }
  }, [group, onBack]);

  // Load data when component mounts
  useEffect(() => {
    // Load employees if not already loaded
    if (!employees?.length) {
      console.log("GroupDetail: Loading employees...");
      dispatch({ type: LOAD_EMPLOYEES });
    }

    // Load employee categories for filtering
    if (get_auth?.organization_id) {
      dispatch({
        type: SG_GET_EMPLOYEE_ORG_CATEGORIES,
        payload: `organization=${get_auth?.organization_id}`,
      });
    }

    // Load survey structure for category options
    dispatch({
      type: SG_GET_SURVEY_STRUCTURES,
      payload: `organization=${get_auth?.organization_id}`,
    });
  }, [dispatch, employees?.length, get_auth?.organization_id]);

  // Always fetch the latest single group on mount or when group.id changes
  useEffect(() => {
    if (group?.id) {
      dispatch({
        type: SG_FETCH_ORGANIZATION_GROUP,
        payload: { id: group.id },
      });
    }
  }, [dispatch, group?.id]);
  // Keep profile fields in sync when manager changes
  useEffect(() => {
    if (manager?.id) {
      setProfileFirstName(manager.first_name || "");
      setProfileLastName(manager.last_name || "");
      dispatch({ type: PULL_EMPLOYEE, payload: { id: manager.id } });
    } else {
      setProfileFirstName("");
      setProfileLastName("");
    }
  }, [manager?.id]);

  useEffect(() => {
    setGroupName(group?.name || "");
    setIsInlineGroupNameEditing(false);
    setInlineGroupNameValue("");
    setInlineGroupNameError("");
  }, [group?.name]);

  const handleSaveProfile = () => {
    if (!manager?.id) return;
    setProfileSaving(true);
    dispatch({
      type: SG_UPDATE_EMPLOYEE_AUDIT,
      payload: {
        id: manager.id,
        first_name: profileFirstName,
        last_name: profileLastName,
      },
    });
    // optimistic UX; a saga typically refreshes employee; keep simple
    setTimeout(() => {
      setProfileSaving(false);
      addToast("Updated profile", { appearance: "success" });
    }, 600);
  };

  const buildSuggestedGroupName = () => {
    const existing = (groupName || group?.name || "").trim();
    if (existing) {
      return existing;
    }

    if (manager) {
      const first = (manager.first_name || "").trim();
      const last = (manager.last_name || "").trim();
      const combined = `${first} ${last}`.trim();
      if (combined) {
        return `${combined} Team`;
      }
      const email = (manager.email || "").trim();
      if (email) {
        return `${email} Team`;
      }
    }

    return `Permission Group - ${new Date().toLocaleString()}`;
  };

  const openGroupNameModal = (action) => {
    setGroupNameTargetAction(action);
    setPendingGroupName(buildSuggestedGroupName());
    setIsGroupNameModalOpen(true);
  };

  const resetGroupNameModalState = () => {
    setIsGroupNameModalOpen(false);
    setGroupNameTargetAction(null);
    setPendingGroupName("");
  };

  const handleGroupNameCancel = () => {
    resetGroupNameModalState();
  };

  const startInlineGroupNameEdit = () => {
    if (!canEditGroupName) return;
    setInlineGroupNameValue(groupName || derivedGroupName || "");
    setInlineGroupNameError("");
    setIsInlineGroupNameEditing(true);
  };

  const cancelInlineGroupNameEdit = () => {
    setIsInlineGroupNameEditing(false);
    setInlineGroupNameValue("");
    setInlineGroupNameError("");
  };

  const handleInlineGroupNameChange = (value) => {
    setInlineGroupNameValue(value);
    if (inlineGroupNameError) {
      setInlineGroupNameError("");
    }
  };

  const saveInlineGroupName = () => {
    if (!canEditGroupName || !group?.id) return;
    const trimmed = (inlineGroupNameValue || "").trim();

    if (!trimmed) {
      setInlineGroupNameError("Group name is required");
      return;
    }

    if (trimmed === groupName) {
      cancelInlineGroupNameEdit();
      return;
    }

    setGroupNameSaving(true);
    dispatch({
      type: SG_EDIT_ORGANIZATION_GROUP,
      payload: { id: group.id, name: trimmed },
    });

    setGroupName(trimmed);
    setIsInlineGroupNameEditing(false);
    setInlineGroupNameValue("");
    setInlineGroupNameError("");
    addToast("Group name updated", { appearance: "success", autoDismiss: true });
    setGroupNameSaving(false);
  };

  const handleInlineGroupNameKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      saveInlineGroupName();
    }
  };


  // Helper function to get employees in this group
  const getEmployeesInGroup = () => {
    if (!group?.id) {
      console.log("getEmployeesInGroup: No group ID");
      return [];
    }

    // If this is a temporary group (login employee who isn't a group manager),
    // return empty array - they shouldn't appear in their own Access Members list
    if (group?.isTemporary) {
      console.log(
        "getEmployeesInGroup: Temporary group, returning empty array"
      );
      return [];
    }

    const effectiveGroup =
      organization_group_detail?.id === group.id
        ? organization_group_detail
        : group;
    console.log(
      "getEmployeesInGroup: Getting members from group",
      effectiveGroup.id
    );
    console.log("getEmployeesInGroup: Group members:", effectiveGroup.members);

    // Use the members field from the effective group directly
    if (Array.isArray(effectiveGroup.members)) {
      const employees = effectiveGroup.members
        .map((member) => {
          const employee = getEmployeeById(member);
          console.log(
            "getEmployeesInGroup: Mapped member",
            member,
            "→",
            employee
          );
          return employee;
        })
        .filter(Boolean);

      console.log("getEmployeesInGroup: Final employees in group:", employees);
      return employees;
    }

    console.log(
      "getEmployeesInGroup: No members array found, returning empty array"
    );
    return [];
  };

  // Handle creating new group with current manager
  const handleCreateGroup = (overrideName = "") => {
    if (!manager) {
      console.error("❌ No manager available for group creation");
      return;
    }

    // Prevent creating a group if this manager is already a manager of another group
    const existingManagerIds = Array.isArray(organization_groups)
      ? organization_groups
          .map((g) => g.manager)
          .filter(Boolean)
          .map((m) => (typeof m === "object" ? m.id : m))
      : [];
    if (existingManagerIds.includes(manager.id)) {
      addToast("Selected contact is already a group manager", {
        appearance: "warning",
        autoDismiss: true,
      });
      return;
    }

    console.log("🔄 Creating group for manager:", manager);

    // Get the manager's name for the group name
    let managerName = "Unknown Manager";
    if (manager.first_name && manager.last_name) {
      managerName = `${manager.first_name} ${manager.last_name}`;
    } else if (manager.email) {
      managerName = manager.email;
    }

    const trimmedOverride = (overrideName || "").trim();
    const fallbackName = `${managerName}`.trim()
      ? managerName
      : `Permission Group - ${new Date().toLocaleString()}`;
    const finalName = trimmedOverride || fallbackName;

    const payload = {
      name: finalName,
      description: "", // Always blank
      organization:
        get_auth?.organization_id || get_auth?.selectedOrganization?.id,
      manager_id: manager.id,
      group_type: "permission",
    };

    console.log("🔄 Creating group with payload:", payload);

    dispatch({
      type: SG_ADD_ORGANIZATION_GROUP,
      payload,
    });

    addToast("Group created successfully", {
      appearance: "success",
      autoDismiss: true,
    });

    // Go back to main list and let parent handle refreshing
    setTimeout(() => {
      onBack();
    }, 1000);
  };

  const handleGroupNameSubmit = (nextName) => {
    const trimmedName = (nextName || "").trim();
    if (!trimmedName) {
      return;
    }

    if (groupNameTargetAction === "addContacts") {
      if (!group?.id) {
        console.warn(
          "Attempted to add contacts without a persisted group identifier"
        );
        resetGroupNameModalState();
        return;
      }

      if (trimmedName !== group?.name) {
        dispatch({
          type: SG_EDIT_ORGANIZATION_GROUP,
          payload: {
            id: group.id,
            name: trimmedName,
          },
        });
      }

      setGroupName(trimmedName);
      resetGroupNameModalState();
      setIsAddEmployeesModalOpen(true);
      return;
    }

    if (groupNameTargetAction === "createGroup") {
      setGroupName(trimmedName);
      resetGroupNameModalState();
      handleCreateGroup(trimmedName);
      return;
    }

    resetGroupNameModalState();
  };

  // Handle adding employees to group - ALWAYS uses bulk API
  const handleAddEmployees = (
    groupId,
    employeeIds,
    isBulkOperation = false
  ) => {
    console.log("🚀 handleAddEmployees called with:", {
      groupId,
      employeeIds,
      isBulkOperation,
      employeeIdsType: typeof employeeIds,
      isArray: Array.isArray(employeeIds),
    });

    if (!Array.isArray(employeeIds) || employeeIds.length === 0) {
      console.error("❌ Invalid employeeIds:", employeeIds);
      return;
    }

    const payload = {
      organization_group_id: groupId,
      employee_ids: employeeIds,
      action: "add",
    };

    console.log(
      "🔥 Dispatching SG_BULK_UPDATE_ORGANIZATION_GROUP with payload:",
      payload
    );

    dispatch({
      type: SG_BULK_UPDATE_ORGANIZATION_GROUP,
      payload,
    });

    addToast(`Added ${employeeIds.length} contact(s) to the group`, {
      appearance: "success",
      autoDismiss: true,
    });

    // Reset pagination and selections when adding members
    setCurrentPage(1);
    setSelectedMembers(new Set());

    // The saga will automatically refresh organization groups after bulk update
  };

  // Handle removing employee from group (single employee)
  const handleRemoveEmployee = (employeeId) => {
    console.log("🗑️ handleRemoveEmployee called with employeeId:", employeeId);
    setDeleteConfirmation({
      isOpen: true,
      employeeIds: [employeeId],
      isBulk: false,
    });
  };

  // Handle bulk removing employees from group
  const handleBulkRemoveEmployees = (employeeIds) => {
    if (!Array.isArray(employeeIds) || employeeIds.length === 0) return;
    setDeleteConfirmation({ isOpen: true, employeeIds, isBulk: true });
  };

  // Multi-select handlers
  const handleMemberSelect = (memberId) => {
    setSelectedMembers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(memberId)) {
        newSet.delete(memberId);
      } else {
        newSet.add(memberId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    // Accumulate selections: add current page's members to existing selections
    const currentPageMemberIds = paginatedMembers.map((member) => member.id);
    setSelectedMembers((prev) => {
      const newSet = new Set(prev);
      currentPageMemberIds.forEach((id) => newSet.add(id));
      return newSet;
    });
  };

  const handleClearSelection = () => {
    setSelectedMembers(new Set());
  };

  const confirmDelete = () => {
    if (deleteConfirmation.employeeIds.length > 0) {
      console.log("🗑️ Confirming removal for:", deleteConfirmation.employeeIds);

      dispatch({
        type: SG_BULK_UPDATE_ORGANIZATION_GROUP,
        payload: {
          organization_group_id: group.id,
          employee_ids: deleteConfirmation.employeeIds,
          action: "remove",
        },
      });

      const count = deleteConfirmation.employeeIds.length;
      addToast(
        `Successfully removed ${count} contact${
          count !== 1 ? "s" : ""
        } from the group`,
        {
          appearance: "success",
          autoDismiss: true,
        }
      );

      // Clear selections and reset pagination
      setSelectedMembers(new Set());
      setCurrentPage(1);

      // The saga will automatically refresh organization groups after bulk update
    }
    setDeleteConfirmation({ isOpen: false, employeeIds: [], isBulk: false });
  };

  const handleRemoveSelected = () => {
    if (selectedMembers.size === 0) return;
    const selectedIds = Array.from(selectedMembers);
    handleBulkRemoveEmployees(selectedIds);
  };



  const groupMembers = getEmployeesInGroup();

  // Tab configuration (show count on Assigned Contacts tab)
  const tabs = [
    "Profile",
    "Permissions",
    `Assigned Contacts (${groupMembers.length})`,
  ];
  
  // Pagination logic for members
  const totalMembers = groupMembers.length;
  const totalPages = Math.ceil(totalMembers / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedMembers = groupMembers.slice(startIndex, endIndex);

  // Reset pagination and selections when group changes
  useEffect(() => {
    setCurrentPage(1);
    setSelectedMembers(new Set());
  }, [group?.id]);

  // Reset to page 1 when items per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const groupBreadcrumbs = useMemo(() => {
    const label = derivedGroupName || "Group Detail";

    return [
      {
        label: "User Management",
        onClick: () => {
          if (typeof onBack === "function") {
            onBack();
          }
        },
      },
      {
        label,
      },
    ];
  }, [derivedGroupName, onBack]);

  if (!group) {
    return null;
  }

  return (
    <Container>
      <AccessBreadcrumb includeRoot={false} crumbs={groupBreadcrumbs} />
      {/* Header Section */}
      <HeaderSection>
        <HeaderContent>
          <IconWrapper>
            <UsersIcon />
          </IconWrapper>
          <HeaderText>
            <Title>
              {manager
                ? `${manager.first_name} ${manager.last_name}`
                : "No Contact Assigned"}
            </Title>
            <Subtitle>
              {manager ? manager.email : "No email available"}
            </Subtitle>
          </HeaderText>
        </HeaderContent>
      </HeaderSection>

      {/* Tabbed Interface */}
      <TabsComponent
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      >
        {activeTab === 0 && (
          <ProfileSection style={{ padding: '24px' }}>
            <ProfileCard>
              <ProfileHeader>Profile</ProfileHeader>
              <ProfileBody>
                <ProfileRow>
                  <ProfileLabel>First Name</ProfileLabel>
                  <ProfileField>
                    <ProfileInput
                      type="text"
                      value={profileFirstName}
                      onChange={(e) => setProfileFirstName(e.target.value)}
                      placeholder="Enter first name"
                    />
                  </ProfileField>
                </ProfileRow>
                <ProfileRow>
                  <ProfileLabel>Last Name</ProfileLabel>
                  <ProfileField>
                    <ProfileInput
                      type="text"
                      value={profileLastName}
                      onChange={(e) => setProfileLastName(e.target.value)}
                      placeholder="Enter last name"
                    />
                  </ProfileField>
                </ProfileRow>
                <ProfileActions>
                  <ProfileSaveButton
                    onClick={handleSaveProfile}
                    disabled={profileSaving || !manager}
                  >
                    {profileSaving ? 'Saving...' : 'Save Profile'}
                  </ProfileSaveButton>
                </ProfileActions>
              </ProfileBody>
            </ProfileCard>
          </ProfileSection>
        )}
        {activeTab === 1 && (
          <ManagerPermissions
            manager={manager}
            group={group}
            isVisible={activeTab === 1}
            onGoToAssignContacts={() => setActiveTab(2)}
          />
        )}
        {activeTab === 2 && (
          <MembersSection style={{ padding: '24px' }}>
            {group && groupMembers.length > 0 && (
              <GroupNameInlineSection>
                <GroupNameInlineLabel>Group Name</GroupNameInlineLabel>
                {isInlineGroupNameEditing && canEditGroupName ? (
                  <GroupNameEditor>
                    <InlineGroupNameInput
                      value={inlineGroupNameValue}
                      onChange={(event) =>
                        handleInlineGroupNameChange(event.target.value)
                      }
                      onKeyDown={handleInlineGroupNameKeyDown}
                      placeholder="Enter group name"
                      autoFocus
                    />
                    {inlineGroupNameError && (
                      <GroupNameError>{inlineGroupNameError}</GroupNameError>
                    )}
                    <GroupNameInlineActions>
                      <SaveButton
                        onClick={saveInlineGroupName}
                        disabled={groupNameSaving}
                      >
                        {groupNameSaving ? "Saving..." : "Save"}
                      </SaveButton>
                      <CancelButton onClick={cancelInlineGroupNameEdit}>
                        Cancel
                      </CancelButton>
                    </GroupNameInlineActions>
                  </GroupNameEditor>
                ) : (
                  <GroupNameDisplay>
                    <GroupNameValue>
                      {derivedGroupName || "Untitled Group"}
                    </GroupNameValue>
                    {canEditGroupName && (
                      <GroupNameEditButton onClick={startInlineGroupNameEdit}>
                        Edit Name
                      </GroupNameEditButton>
                    )}
                  </GroupNameDisplay>
                )}
              </GroupNameInlineSection>
            )}
            <SectionHeader>
              <SectionTitle>Assigned Contacts ({groupMembers.length})</SectionTitle>
              <SectionActions>
            {selectedMembers.size > 0 && (
              <>
                <SelectionInfo>
                  {selectedMembers.size} selected
                </SelectionInfo>
                <ActionButton onClick={handleRemoveSelected} danger>
                  <DeleteIcon />
                  Remove Selected
                </ActionButton>
              </>
            )}
            <SelectionControls>
              <ActionButton onClick={handleSelectAll} disabled={paginatedMembers.length === 0}>
                Select All
              </ActionButton>
              <ActionButton onClick={handleClearSelection} disabled={selectedMembers.size === 0}>
                Clear
              </ActionButton>
            </SelectionControls>
                <ActionButton
                  onClick={() => setIsRemoveByTagModalOpen(true)}
                  style={{ background: "#ef4444", color: "white" }}
                >
                  <DeleteIcon />
                  Remove by Tag
                </ActionButton>
            <AddMembersButton onClick={() => openGroupNameModal("addContacts")}>
              <PlusIcon />
              Add Contacts to Group
            </AddMembersButton>
          </SectionActions>
        </SectionHeader>

            <TableSection>
              {groupMembers.length > 0 ? (
                <MembersTable>
                  <TableHeader>
                    <TableHeaderRow>
                      <TableHeaderCell>
                        <Checkbox
                          checked={
                            paginatedMembers.length > 0 &&
                            paginatedMembers.every((member) => selectedMembers.has(member.id))
                          }
                          onChange={handleSelectAll}
                          disabled={paginatedMembers.length === 0}
                        />
                      </TableHeaderCell>
                      <TableHeaderCell>Contact</TableHeaderCell>
                      <TableHeaderCell>Email</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                      <TableHeaderCell>Actions</TableHeaderCell>
                    </TableHeaderRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedMembers.map((member) => (
                      <TableRow key={member.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedMembers.has(member.id)}
                            onChange={() => handleMemberSelect(member.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <EmployeeInfo>
                            <EmployeeName>
                              {member.first_name} {member.last_name}
                            </EmployeeName>
                          </EmployeeInfo>
                        </TableCell>
                        <TableCell>
                          <EmployeeEmail>{member.email}</EmployeeEmail>
                        </TableCell>
                        <TableCell>
                          <StatusBadge enabled={member.status === 1}>
                            {member.status === 1 ? "Active" : "Inactive"}
                          </StatusBadge>
                        </TableCell>
                        <TableCell>
                          <RemoveButton
                            onClick={() => {
                              handleRemoveEmployee(member.id);
                            }}
                            onMouseEnter={() =>
                              console.log(
                                "Mouse entered remove button for:",
                                member.first_name
                              )
                            }
                            title="Remove from group"
                            style={{
                              cursor: "pointer",
                              pointerEvents: "auto",
                              zIndex: 1,
                            }}
                          >
                            <DeleteIcon />
                          </RemoveButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </MembersTable>
              ) : (
                <EmptyMembersState>
                  <EmptyIcon>
                    <UsersIcon />
                  </EmptyIcon>
                  {group?.isTemporary ? (
                    <>
                      <EmptyTitle>Not a Group Manager</EmptyTitle>
                      <EmptyText>
                        This contact has login access but is not currently a
                        group manager. Create a new group to manage team
                        members.
                      </EmptyText>
                      <EmptyButton onClick={() => openGroupNameModal("createGroup")}>
                        <PlusIcon />
                        Create Group
                      </EmptyButton>
                    </>
                  ) : (
                    <>
                      <EmptyTitle>No Assigned Contacts Yet</EmptyTitle>
                      <EmptyText>
                        This group doesn't have any contacts yet. Add contacts
                        to get started.
                      </EmptyText>
                      <EmptyButton
                        onClick={() => openGroupNameModal("addContacts")}
                      >
                        <PlusIcon />
                        Add First Contact to Group
                      </EmptyButton>
                    </>
                  )}
                </EmptyMembersState>
              )}
            </TableSection>

            {/* Pagination for Members */}
            {totalMembers > 0 && (
              <PaginationSection>
                <PaginationInfo>
                  Showing {startIndex + 1} to {Math.min(endIndex, totalMembers)}{" "}
                  of {totalMembers} contacts
                </PaginationInfo>
                <PaginationControls>
                  <ItemsPerPageContainer>
                    <ItemsPerPageLabel>Show:</ItemsPerPageLabel>
                    <Dropdown
                      selection
                      compact
                      value={pageSize}
                      onChange={(e, { value }) => setPageSize(value)}
                      options={[
                        { key: 10, text: '10', value: 10 },
                        { key: 50, text: '50', value: 50 },
                        { key: 100, text: '100', value: 100 },
                      ]}
                    />
                  </ItemsPerPageContainer>
                  {totalPages > 1 && (
                    <>
                      <PaginationButton
                        onClick={() =>
                          setCurrentPage((prev) => Math.max(1, prev - 1))
                        }
                        disabled={currentPage === 1}
                      >
                        Previous
                      </PaginationButton>
                      <PageNumber>
                        {currentPage} of {totalPages}
                      </PageNumber>
                      <PaginationButton
                        onClick={() =>
                          setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                        }
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </PaginationButton>
                    </>
                  )}
                </PaginationControls>
              </PaginationSection>
            )}
          </MembersSection>
        )}
      </TabsComponent>

      <GroupNameModal
        isOpen={isGroupNameModalOpen}
        initialValue={pendingGroupName}
        onCancel={handleGroupNameCancel}
        onSubmit={handleGroupNameSubmit}
      />

      {/* Add Employees Modal */}
      <AddEmployeesModal
        isOpen={isAddEmployeesModalOpen}
        onClose={() => setIsAddEmployeesModalOpen(false)}
        group={group}
        groupName={groupName}
        employees={employees}
        onAddEmployees={handleAddEmployees}
      />

      {/* Remove Contacts by Tag Modal */}
      <RemoveContactsByTagModal
        isOpen={isRemoveByTagModalOpen}
        onClose={() => setIsRemoveByTagModalOpen(false)}
        group={group}
        groupName={groupName}
        currentGroupMembers={groupMembers}
        employees={employees}
      />

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteConfirmation.isOpen}
        onClose={() =>
          setDeleteConfirmation({
            isOpen: false,
            employeeIds: [],
            isBulk: false,
          })
        }
        onConfirm={confirmDelete}
        title="Remove Contact Access"
        message={
          deleteConfirmation.isBulk
            ? `Are you sure you want to remove ${
                deleteConfirmation.employeeIds.length
              } contact${
                deleteConfirmation.employeeIds.length !== 1 ? "s" : ""
              } from the group? This action cannot be undone.`
            : "Are you sure you want to remove this contact from the group? This action cannot be undone."
        }
        confirmText="Remove"
        cancelText="Cancel"
      />
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  margin-bottom: 32px;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
`;

const HeaderText = styled.div``;

const Title = styled.h1`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 32px;
  color: #414141;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 16px;
  color: #7e7e7e;
  margin: 0;
`;



const MembersSection = styled.div``;

const GroupNameInlineSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  margin-bottom: 24px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
`;

const GroupNameInlineLabel = styled.span`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #6b7280;
  flex: 0 0 auto;
`;

const GroupNameDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1 1 auto;
  min-width: 200px;
`;

const GroupNameValue = styled.span`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 18px;
  color: #111827;
  word-break: break-word;
`;

const GroupNameEditButton = styled.button`
  padding: 8px 14px;
  background: #476dfa;
  border: none;
  border-radius: 6px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background: #3555c5;
    transform: translateY(-1px);
  }
`;

const GroupNameEditor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1 1 320px;
`;

const InlineGroupNameInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-family: "Poppins";
  font-size: 16px;
  color: #111827;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
  }
`;

const GroupNameInlineActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

// Profile Tab Styles
const ProfileSection = styled.div``;

const ProfileCard = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
`;

const ProfileHeader = styled.div`
  padding: 16px 20px;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #374151;
  background: #f8f9fa;
  border-bottom: 1px solid #e5e7eb;
`;

const ProfileBody = styled.div`
  padding: 16px 20px;
`;

const ProfileRow = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
`;

const ProfileLabel = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
`;

const ProfileField = styled.div``;

const ProfileInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-family: "Poppins";
  font-size: 14px;
  background: #fafafa;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    background: white;
    border-color: #476dfa;
    box-shadow: 0 0 0 3px rgba(71, 109, 250, 0.1);
  }
`;

const ProfileActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 12px;
`;

const ProfileSaveButton = styled.button`
  background: #10b981;
  border: none;
  border-radius: 8px;
  color: white;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  padding: 12px 20px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #059669;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(16, 185, 129, 0.3);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const SectionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SelectionInfo = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #476dfa;
  padding: 8px 12px;
  background: #f0f4ff;
  border-radius: 6px;
`;

const SelectionControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${props => props.danger ? '#ff4444' : '#f8f9fa'};
  border: 1px solid ${props => props.danger ? '#ff4444' : '#e0e0e0'};
  border-radius: 6px;
  color: ${props => props.danger ? 'white' : '#414141'};
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover:not(:disabled) {
    background: ${props => props.danger ? '#cc3333' : '#e9ecef'};
    transform: translateY(-1px);
  }
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #476dfa;
`;

const SectionTitle = styled.h2`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 24px;
  color: #414141;
  margin: 0;
`;

const AddMembersButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #476dfa;
  border: none;
  border-radius: 8px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3d5ce6;
  }
`;

const TableSection = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const MembersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: #f8f9fa;
`;

const TableHeaderRow = styled.tr``;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  border-bottom: 1px solid #e0e0e0;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  &:hover {
    background: #f8f9fa;
  }
`;

const TableCell = styled.td`
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: top;
`;

const EmployeeInfo = styled.div``;



const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 16px;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  background: ${props => props.enabled ? '#e8f5e8' : '#f5f5f5'};
  color: ${props => props.enabled ? '#2e7d32' : '#757575'};
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: #ff4444;
  border: none;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #cc3333;
    transform: translateY(-1px);
  }
`;

// Pagination Styles
const PaginationSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  background: white;
  border-top: 1px solid #e0e0e0;
  margin-top: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const PaginationInfo = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
  color: #7e7e7e;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ItemsPerPageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ItemsPerPageLabel = styled.span`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #6b7280;
  white-space: nowrap;
`;

const PaginationButton = styled.button`
  padding: 8px 16px;
  background: ${props => props.disabled ? '#f0f0f0' : '#476dfa'};
  border: none;
  border-radius: 6px;
  color: ${props => props.disabled ? '#999' : 'white'};
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #3d5ce6;
    transform: translateY(-1px);
  }
`;

const PageNumber = styled.div`
  padding: 8px 16px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  min-width: 60px;
  text-align: center;
`;

const EmptyMembersState = styled.div`
  padding: 64px 32px;
  text-align: center;
`;

const EmptyState = styled.div`
  padding: 64px 32px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  opacity: 0.5;
`;



const EmptyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #476dfa;
  border: none;
  border-radius: 8px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3d5ce6;
  }
`;

// Modal Styles (reused)
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const GroupNameModalContent = styled(ModalContent)`
  max-width: 420px;
  max-height: none;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e0e0e0;
`;

const ModalTitle = styled.h2`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 20px;
  color: #414141;
  margin: 0;
`;

const HeaderActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
    border-color: #c0c0c0;
    color: #414141;
  }
`;

const SaveButton = styled.button`
  padding: 10px 20px;
  background: #10b981;
  border: none;
  border-radius: 8px;
  color: white;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #059669;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(16, 185, 129, 0.3);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #7e7e7e;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;

  &:hover {
    background: #f0f0f0;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
`;

const GroupNameBody = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const GroupNameDescription = styled.p`
  margin: 0;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
`;

const GroupNameInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-family: "Poppins";
  font-size: 16px;
  color: #111827;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
  }
`;

const GroupNameError = styled.span`
  font-family: "Poppins";
  font-size: 13px;
  color: #dc2626;
`;

const GroupNameActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px 24px;
  border-top: 1px solid #e5e7eb;
`;

const SearchContainer = styled.div`
  margin-bottom: 24px;
`;

const SearchLabel = styled.label`
  display: block;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin-bottom: 8px;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
  z-index: 1;
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 14px 16px 14px 44px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-family: "Poppins";
  font-size: 14px;
  background: #fafafa;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #476dfa;
    background: white;
    box-shadow: 0 0 0 3px rgba(71, 109, 250, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const SearchHelpText = styled.div`
  margin-top: 8px;
`;

const SearchResultsCount = styled.div`
  font-family: "Poppins";
  font-size: 13px;
  color: #10b981;
  font-weight: 600;
`;

const TotalEmployeesCount = styled.div`
  font-family: "Poppins";
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
`;

const SearchPromptState = styled.div`
  text-align: center;
  padding: 60px 32px;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border-radius: 16px;
  border: 2px dashed #d1d5db;
  margin-bottom: 24px;
`;

const SearchPromptIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
  opacity: 0.8;
`;

const SearchPromptTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 700;
  font-size: 20px;
  color: #1f2937;
  margin: 0 0 12px 0;
`;

const SearchPromptText = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 15px;
  color: #6b7280;
  margin: 0 0 24px 0;
  line-height: 1.6;
`;

const SearchPromptFeatures = styled.div`
  text-align: left;
  display: inline-block;
  margin-top: 8px;
`;

const SearchFeature = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #4b5563;
  margin-bottom: 6px;
  line-height: 1.4;
`;

const EmployeesList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EmployeeItem = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 20px;
  background: white;
  border: 2px solid ${props => props.selected ? '#476dfa' : '#e0e0e0'};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    border-color: ${props => props.selected ? '#3d5ce6' : '#476dfa'};
    background: ${props => props.selected ? '#f0f8ff' : '#f8f9fa'};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(71, 109, 250, 0.1);
  }

  ${props => props.selected && `
    background: #f0f8ff;
    box-shadow: 0 2px 8px rgba(71, 109, 250, 0.15);
  `}
`;

const EmployeeCheckbox = styled.input`
  width: 20px;
  height: 20px;
  margin-right: 16px;
  accent-color: #476dfa;
  cursor: pointer;
`;

const EmployeeDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const EmployeeName = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #1f2937;
  line-height: 1.4;
`;

const EmployeeEmail = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.3;
`;

const ClickableEmployeeName = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #3b82f6;
  line-height: 1.4;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #2563eb;
    text-decoration: underline;
  }
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 11px;
  background: ${props => props.isGroupMember ? '#dcfce7' : '#dbeafe'};
  color: ${props => props.isGroupMember ? '#166534' : '#1e40af'};
`;

const ManagePermissionsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #3b82f6;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #3b82f6;

  &:hover {
    background: #3b82f6;
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
  }
`;

const EmployeeRole = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SelectedIndicator = styled.div`
  width: 24px;
  height: 24px;
  background: #476dfa;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  margin-left: 12px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;



// Full Screen Modal Styles
const EnhancedModalOverlay = styled(ModalOverlay)`
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(8px);
`;

const EnhancedModalContent = styled(ModalContent)`
  width: 100vw;
  height: 100vh;
  max-width: none;
  max-height: none;
  border-radius: 0;
  display: flex;
  flex-direction: column;
`;

const EnhancedModalBody = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
  padding: 0;
`;

const LeftPanel = styled.div`
  flex: 1;
  background: #f8fafc;
  border-right: 2px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const RightPanel = styled.div`
  flex: 1;
  background: white;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  padding: 24px;
  border-bottom: 2px solid #e2e8f0;
  background: white;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const PanelTitle = styled.h2`
  font-family: "Poppins";
  font-weight: 700;
  font-size: 20px;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const PanelSubtitle = styled.p`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #64748b;
  margin: 0;
`;

const ClearAllButton = styled.button`
  background: #ef4444;
  border: none;
  border-radius: 6px;
  color: white;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 12px;
  padding: 6px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: auto;

  &:hover {
    background: #dc2626;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(239, 68, 68, 0.3);
  }
`;

const PanelContent = styled.div`
  flex: 1;
  padding: 24px 24px 100px 24px; /* Extra bottom padding to prevent action bar overlap */
  overflow-y: auto;
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 24px;
`;

const Tab = styled.button`
  flex: 1;
  padding: 16px 20px;
  background: ${props => props.active ? '#f8f9fa' : 'transparent'};
  border: none;
  border-bottom: 2px solid ${props => props.active ? '#476dfa' : 'transparent'};
  font-family: "Poppins";
  font-weight: ${props => props.active ? '600' : '400'};
  font-size: 14px;
  color: ${props => props.active ? '#476dfa' : '#7e7e7e'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    color: #476dfa;
  }
`;

const ModalEmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  background: #fafafa;
  border-radius: 12px;
  border: 2px dashed #e0e0e0;
`;

const EmptySearchIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const EmptyTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 18px;
  color: #374151;
  margin: 0 0 8px 0;
`;

const EmptyText = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

// Bulk Email Styles
const BulkEmailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const BulkEmailHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const BulkEmailIcon = styled.div`
  font-size: 32px;
  flex-shrink: 0;
  margin-top: 4px;
`;

const BulkEmailTitleSection = styled.div`
  flex: 1;
`;

const BulkEmailLabel = styled.h2`
  font-family: "Poppins";
  font-weight: 700;
  font-size: 20px;
  color: #1f2937;
  margin: 0 0 4px 0;
`;

const BulkEmailHint = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const BulkEmailInstructions = styled.div`
  background: #f0f8ff;
  border: 2px solid #e0e7ff;
  border-radius: 12px;
  padding: 20px;
`;

const InstructionTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #1e40af;
  margin: 0 0 12px 0;
`;

const FormatExamples = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormatExample = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #4b5563;
  line-height: 1.4;
`;

const BulkEmailTextarea = styled.textarea`
  width: 100%;
  min-height: 160px;
  padding: 16px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-family: "Poppins";
  font-size: 14px;
  background: #fafafa;
  resize: vertical;
  line-height: 1.5;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #476dfa;
    background: white;
    box-shadow: 0 0 0 3px rgba(71, 109, 250, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
    line-height: 1.5;
  }
`;

const BulkEmailActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BulkEmailStats = styled.div`
  display: flex;
  align-items: center;
`;

const EmailCount = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #10b981;
  padding: 8px 16px;
  background: #ecfdf5;
  border: 2px solid #a7f3d0;
  border-radius: 8px;
`;

const ProcessEmailsButton = styled.button`
  padding: 14px 28px;
  background: ${props => props.disabled ? '#d1d5db' : '#476dfa'};
  border: none;
  border-radius: 10px;
  color: white;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(71, 109, 250, 0.2);

  &:hover:not(:disabled) {
    background: #3d5ce6;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(71, 109, 250, 0.3);
  }
`;

const InvalidEmailsSection = styled.div`
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 2px solid #f59e0b;
  border-radius: 12px;
  padding: 20px;
`;

const InvalidEmailsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const InvalidEmailsIcon = styled.div`
  font-size: 24px;
`;

const InvalidEmailsTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 700;
  font-size: 16px;
  color: #92400e;
  margin: 0;
`;

const InvalidEmailsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`;

const InvalidEmail = styled.span`
  background: #fed7aa;
  color: #9a3412;
  padding: 8px 12px;
  border-radius: 8px;
  font-family: "Poppins";
  font-size: 13px;
  font-weight: 500;
  border: 1px solid #fdba74;
`;

const InvalidEmailsHint = styled.div`
  font-family: "Poppins";
  font-size: 14px;
  color: #92400e;
  line-height: 1.5;
  font-weight: 500;
`;

// Category Filter Styles
const CategorySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const CategoryIcon = styled.div`
  font-size: 32px;
  flex-shrink: 0;
  margin-top: 4px;
`;

const CategoryTitleSection = styled.div`
  flex: 1;
`;

const CategoryLabel = styled.h2`
  font-family: "Poppins";
  font-weight: 700;
  font-size: 20px;
  color: #1f2937;
  margin: 0 0 4px 0;
`;

const CategoryHint = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const CategoryFilters = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

/* Updated Category Group - now defined in new styles section */

/* Updated Category Group Header - now defined in new styles section */
/* Updated Category Name - now defined in new styles section */  
/* Updated Category Count - now defined in new styles section */
/* Updated Category Options - now defined in new styles section */
/* Updated Category Option - now defined in new styles section */
/* Updated Category Option Checkbox - now defined in new styles section */
/* Updated Category Option Text - now defined in new styles section */

const CategorySelectedIcon = styled.div`
  width: 20px;
  height: 20px;
  background: #3b82f6;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
`;

const CategoryEmptyState = styled.div`
  text-align: center;
  padding: 60px 32px;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border-radius: 16px;
  border: 2px dashed #d1d5db;
`;

const CategoryEmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
  opacity: 0.6;
`;

const CategoryEmptyTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 700;
  font-size: 20px;
  color: #1f2937;
  margin: 0 0 12px 0;
`;

const CategoryEmptyText = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 15px;
  color: #6b7280;
  margin: 0;
  line-height: 1.6;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

const CategoryResults = styled.div`
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfccb 100%);
  border: 2px solid #22c55e;
  border-radius: 16px;
  padding: 24px;
`;

const CategoryResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  gap: 16px;
`;

const CategoryResultsInfo = styled.div`
  flex: 1;
`;

const CategoryResultsTitle = styled.h4`
  font-family: "Poppins";
  font-weight: 700;
  font-size: 18px;
  color: #15803d;
  margin: 0 0 4px 0;
`;

const CategoryResultsSubtitle = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #16a34a;
`;

const CategoryResultsActions = styled.div`
  display: flex;
  align-items: center;
`;

const SelectAllCategoryButton = styled.button`
  background: #22c55e;
  border: none;
  border-radius: 10px;
  color: white;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  padding: 12px 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(34, 197, 94, 0.2);

  &:hover {
    background: #16a34a;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(34, 197, 94, 0.3);
  }
`;

const CategoryEmployeesList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// Selected Employees Summary
const SelectedEmployeesSummary = styled.div`
  background: linear-gradient(135deg, #f0f8ff 0%, #e6f3ff 100%);
  border: 2px solid #476dfa;
  border-radius: 16px;
  padding: 20px;
  margin-top: 24px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg, #476dfa, #3d5ce6);
    border-radius: 16px;
    z-index: -1;
    opacity: 0.1;
  }
`;

const SummaryTitle = styled.h4`
  font-family: "Poppins";
  font-weight: 700;
  font-size: 16px;
  color: #1e40af;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '✓';
    background: #476dfa;
    color: white;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 600;
  }
`;

const SelectedEmployeesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const SelectedEmployeeItem = styled.div`
  background: white;
  border: 2px solid #e0e7ff;
  border-radius: 10px;
  padding: 12px 16px;
  font-family: "Poppins";
  font-size: 13px;
  color: #1f2937;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 2px 4px rgba(71, 109, 250, 0.1);
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(71, 109, 250, 0.15);
  }
`;

const RemoveSelectedButton = styled.button`
  background: #ef4444;
  border: none;
  border-radius: 50%;
  color: white;
  width: 22px;
  height: 22px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #dc2626;
    transform: scale(1.1);
  }
`;

// Category Sub-Tabs Styles
const CategorySubTabs = styled.div`
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 24px;
`;

const CategorySubTab = styled.button`
  background: ${props => props.active ? '#f3f4f6' : 'transparent'};
  border: none;
  border-bottom: 3px solid ${props => props.active ? '#3b82f6' : 'transparent'};
  color: ${props => props.active ? '#1e40af' : '#6b7280'};
  font-family: "Poppins";
  font-weight: ${props => props.active ? '600' : '500'};
  font-size: 14px;
  padding: 12px 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;

  &:hover {
    background: #f9fafb;
    color: #374151;
  }
`;

const CategorySubTabIcon = styled.span`
  font-size: 16px;
`;

const CategorySubTabBadge = styled.span`
  background: #3b82f6;
  color: white;
  border-radius: 10px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  min-width: 20px;
  text-align: center;
`;

// Updated Category Group Styles (smaller, collapsible)
const CategoryGroup = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 8px;
`;

const CategoryGroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.2s ease;

  &:hover {
    background: #f3f4f6;
  }
`;

const CategoryGroupLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CategoryCollapseIcon = styled.div`
  font-size: 12px;
  color: #6b7280;
  cursor: pointer;
  transition: transform 0.2s ease;
  user-select: none;
  width: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotate(${props => props.expanded ? '0deg' : '0deg'});
`;

const CategoryName = styled.h4`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #1f2937;
  margin: 0;
`;

const CategoryCount = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #6b7280;
  background: #e5e7eb;
  padding: 2px 8px;
  border-radius: 6px;
`;

const CategoryOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 16px 16px 16px;
`;

const CategoryOption = styled.button`
  background: ${props => props.selected ? '#dbeafe' : 'white'};
  color: ${props => props.selected ? '#1e40af' : '#4b5563'};
  border: 1px solid ${props => props.selected ? '#3b82f6' : '#d1d5db'};
  border-radius: 6px;
  padding: 6px 12px;
  font-family: "Poppins";
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;

  &:hover {
    background: ${props => props.selected ? '#bfdbfe' : '#f3f4f6'};
    transform: translateY(-1px);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
`;

const CategoryOptionCheckbox = styled.input`
  width: 14px;
  height: 14px;
  accent-color: #3b82f6;
`;

const CategoryOptionText = styled.span`
  flex: 1;
  text-align: left;
`;

// Category Filter Summary
const CategoryFilterSummary = styled.div`
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border: 2px solid #10b981;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
`;

const CategoryFilterResults = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #047857;
`;

const CategoryActionButtons = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const AddSelectedButton = styled.button`
  padding: 12px 20px;
  background: #10b981;
  border: none;
  border-radius: 8px;
  color: white;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #059669;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(16, 185, 129, 0.3);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

// Category Top Action Bar Styles
const CategoryTopActionBar = styled.div`
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 2px solid #0ea5e9;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  animation: slideDown 0.3s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const CategoryTopActionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CategoryTopActionCount = styled.div`
  font-family: "Poppins";
  font-weight: 700;
  font-size: 18px;
  color: #0c4a6e;
`;

const CategoryTopActionHint = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #0369a1;
`;

const CategoryTopActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #0ea5e9;
  border: none;
  border-radius: 8px;
  color: white;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #0284c7;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(14, 165, 233, 0.3);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

// Category Add Section Styles
const CategoryAddSection = styled.div`
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border: 2px solid #10b981;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  animation: slideDown 0.3s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const CategoryAddInfo = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #047857;
`;

const CategoryAddButton = styled.button`
  padding: 12px 20px;
  background: #10b981;
  border: none;
  border-radius: 8px;
  color: white;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #059669;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(16, 185, 129, 0.3);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const BulkCategoryBar = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  background: #f9fafb;
`;

const BulkCategoryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const BulkCategoryLabel = styled.div`
  font-weight: 600;
  font-size: 14px;
`;

const BulkCategorySelectors = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
  margin-top: 4px;
`;

const BulkSelect = styled.div`
  flex: 1;
`;

const BulkSelectLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
`;

const BulkNativeSelect = styled.select`
  width: 100%;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 13px;
`;

const BulkCategoryActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 4px;
  margin-bottom: 8px;
`;

const BulkMessage = styled.div`
  font-size: 12px;
  color: #4b5563;
  margin-top: 4px;
`;

const BulkConfirmMessage = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #059669;
`;

const CategoryRemoveButton = styled(CategoryAddButton)`
  background: #ef4444;
  &:hover:not(:disabled) {
    background: #dc2626;
    box-shadow: 0 4px 8px rgba(239, 68, 68, 0.3);
  }
`;

const BulkRemoveMessage = styled(BulkConfirmMessage)`
  color: #ef4444;
`;

// Remove Contacts by Tag Modal Styles
const RemoveModalContent = styled(ModalContent)`
  max-width: 1200px;
  width: 95%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
`;

const RemoveModalBody = styled.div`
  flex: 1;
  padding: 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const CategorySelectionSection = styled.div`
  padding: 20px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const CategorySelectionLabel = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #1f2937;
  margin-bottom: 16px;
`;

const CategorySelectionRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`;

const CategorySelectWrapper = styled.div`
  flex: 1;
`;

const CategorySelectLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
  font-weight: 500;
`;

const CategoryPreviewButton = styled(CategoryRemoveButton)`
  width: 100%;
  justify-content: center;
`;

const PreviewSections = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  margin-top: 8px;
`;

const PreviewColumn = styled.div`
  display: flex;
  flex-direction: column;
  background: ${props => props.remove ? '#fef2f2' : '#f9fafb'};
  border: 1px solid ${props => props.remove ? '#fee2e2' : '#e5e7eb'};
  border-radius: 8px;
  overflow: hidden;
`;

const PreviewColumnHeader = styled.div`
  padding: 12px 16px;
  background: ${props => props.remove ? '#fee2e2' : '#f3f4f6'};
  border-bottom: 1px solid ${props => props.remove ? '#fecaca' : '#e5e7eb'};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PreviewColumnTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #1f2937;
`;

const PreviewColumnCount = styled.div`
  font-family: "Poppins";
  font-weight: 700;
  font-size: 16px;
  color: ${props => props.remove ? '#dc2626' : '#1f2937'};
`;

const PreviewList = styled.div`
  flex: 1;
  max-height: 400px;
  overflow-y: auto;
  padding: 8px;
`;

const PreviewListItem = styled.div`
  padding: 10px 12px;
  background: ${props => props.remove ? '#fef2f2' : 'white'};
  border: 1px solid ${props => props.remove ? '#fecaca' : '#e5e7eb'};
  border-radius: 6px;
  margin-bottom: 8px;
  transition: all 0.2s;

  &:last-child {
    margin-bottom: 0;
  }
`;

const PreviewItemName = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 13px;
  color: #1f2937;
  margin-bottom: 4px;
`;

const PreviewItemEmail = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #6b7280;
`;

const PreviewMessage = styled.div`
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #991b1b;
  font-family: "Poppins";
  font-size: 14px;
  font-weight: 500;
`;

const RemoveModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e5e7eb;
  background: white;
`;

const RemoveConfirmButton = styled(CategoryRemoveButton)`
  min-width: 180px;
`;

// Selected Tab Styles
const CategorySelectedTab = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CategorySelectedHeader = styled.div`
  text-align: center;
  padding: 20px;
  background: linear-gradient(135deg, #f0f8ff 0%, #e6f3ff 100%);
  border-radius: 12px;
  border: 2px solid #3b82f6;
`;

const CategorySelectedTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 700;
  font-size: 20px;
  color: #1e40af;
  margin: 0 0 4px 0;
`;

const CategorySelectedSubtitle = styled.p`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #3b82f6;
  margin: 0;
`;

const CategorySelectedEmployeesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
`;

const SelectedEmployeeCard = styled.div`
  background: white;
  border: 2px solid #e0e7ff;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;

  &:hover {
    border-color: #3b82f6;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
  }
`;

const SelectedEmployeeDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SelectedEmployeeName = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #1f2937;
`;

const SelectedEmployeeEmail = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #6b7280;
`;

const SelectedEmployeeRole = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const RemoveSelectedEmployeeButton = styled.button`
  background: #ef4444;
  border: none;
  border-radius: 50%;
  color: white;
  width: 28px;
  height: 28px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #dc2626;
    transform: scale(1.1);
  }
`;

// Selected Empty State
const CategorySelectedEmpty = styled.div`
  text-align: center;
  padding: 60px 32px;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border-radius: 16px;
  border: 2px dashed #d1d5db;
`;

const CategorySelectedEmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
  opacity: 0.6;
`;

const CategorySelectedEmptyTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 700;
  font-size: 20px;
  color: #1f2937;
  margin: 0 0 12px 0;
`;

const CategorySelectedEmptyText = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 15px;
  color: #6b7280;
  margin: 0 0 24px 0;
  line-height: 1.6;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

const CategorySelectedEmptyButton = styled.button`
  background: #3b82f6;
  border: none;
  border-radius: 8px;
  color: white;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  padding: 12px 24px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
  }
`;

// Fixed Action Bar Styles
const FixedActionBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 2px solid #e5e7eb;
  padding: 16px 24px;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
`;

const ActionBarContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ActionBarLeft = styled.div`
  display: flex;
  align-items: center;
`;

const ActionBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SelectedEmployeesIndicator = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #10b981;
  background: #ecfdf5;
  padding: 8px 16px;
  border: 2px solid #a7f3d0;
  border-radius: 8px;
`;

// Selected Employees Grid
const SelectedEmployeesGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr;
`;

// Empty Selected State
const EmptySelectedState = styled.div`
  text-align: center;
  padding: 80px 40px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  border: 2px dashed #cbd5e1;
`;

const EmptySelectedIcon = styled.div`
  font-size: 64px;
  margin-bottom: 24px;
  opacity: 0.5;
`;

const EmptySelectedTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 700;
  font-size: 22px;
  color: #334155;
  margin: 0 0 12px 0;
`;

const EmptySelectedText = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 16px;
  color: #64748b;
  margin: 0;
  line-height: 1.6;
  max-width: 350px;
  margin-left: auto;
  margin-right: auto;
`;

// Confirmation Dialog Styles
const ConfirmationContent = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  max-width: 480px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const ConfirmationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 0 24px;
`;

const ConfirmationTitle = styled.h2`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 20px;
  color: #414141;
  margin: 0;
`;

const ConfirmationBody = styled.div`
  padding: 16px 24px 24px 24px;
`;

const ConfirmationMessage = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 16px;
  color: #7e7e7e;
  margin: 0;
  line-height: 1.5;
`;const ConfirmationActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 0 24px 24px 24px;
`;const ConfirmButton = styled.button`
  padding: 12px 24px;
  background: #dc3545;
  border: none;
  border-radius: 8px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #c82333;
    transform: translateY(-1px);
  }
`;



export default GroupDetail;
